/**
 * UserMention — inline node Tiptap cu chip „& email · role".
 *
 * Trigger: `&` urmat de query (vezi UserMenu.tsx). Evităm clash cu `@`
 * care e deja ocupat de CredentialMention.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

export interface UserMentionAttrs {
  userId: string;
  email: string;
  role?: string;
}

export const UserMentionNode = Node.create({
  name: 'userMention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      userId: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-user-id'),
      },
      email: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-email'),
      },
      role: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-role'),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-user-mention]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-user-mention': '' },
        {
          'data-user-id': HTMLAttributes.userId,
          'data-email': HTMLAttributes.email,
          'data-role': HTMLAttributes.role,
        },
      ),
      `&${HTMLAttributes.email}`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(UserMentionView);
  },
});

function UserMentionView({ node }: NodeViewProps) {
  const { t } = useTranslation();
  const email = (node.attrs.email as string) || '?';
  const role = (node.attrs.role as string) || '';
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as globalThis.Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const initials = email.slice(0, 2).toUpperCase();

  return (
    <NodeViewWrapper
      as="span"
      contentEditable={false}
      draggable
      data-drag-handle
      className="inline-flex items-center align-baseline"
    >
      <span
        ref={ref}
        className="relative inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-mono"
        style={{
          background: 'var(--accent-bg)',
          border: '1px solid var(--accent)',
          color: 'var(--accent)',
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 cursor-pointer"
          title={t('notes_blocks.user_mention.details_title')}
        >
          <span
            className="inline-grid place-items-center w-4 h-4 rounded-full text-[9px] font-semibold"
            style={{ background: 'var(--accent)', color: 'var(--bg)' }}
          >
            {initials}
          </span>
          <span>{email}</span>
        </button>
        {open && (
          <span
            className="absolute left-0 top-full mt-1 z-50 rounded-md shadow-lg p-3 min-w-[240px] text-left"
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text)',
              userSelect: 'text',
            }}
          >
            <span className="block text-[11px] space-y-1">
              <span className="block">
                <span style={{ color: 'var(--text-muted)' }}>{t('notes_blocks.user_mention.email')}</span>{' '}
                <span className="font-mono break-all">{email}</span>
              </span>
              {role && (
                <span className="block">
                  <span style={{ color: 'var(--text-muted)' }}>{t('notes_blocks.user_mention.role')}</span>{' '}
                  <span className="font-mono">{role}</span>
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-2 block text-[10px] hover:underline"
              style={{ color: 'var(--text-muted)' }}
            >
              {t('common.close')}
            </button>
          </span>
        )}
      </span>
    </NodeViewWrapper>
  );
}
